import { useStore } from '@stores';
import { InventoryDateField, InventoryService } from '@services';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useInventory = () => {
  const { inventoryStore } = useStore();
  const {
    inventories,
    totalCount,
    loading,
    currentPage,
    skipCount,
    maxResultCount,
    setSkipCount,
    setCurrentPage,
  } = inventoryStore;

  const [name, setName] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [partNumber, setPartNumber] = useState<string>('');
  const [nominalCode, setNominalCode] = useState<string>('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState<string>('');
  const [categoryIds, setCategoryIds] = useState<string[] | undefined>();
  const [supplierIds, setSupplierIds] = useState<string[] | undefined>();
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  const [dateField, setDateField] = useState<InventoryDateField>('DateAdded');
  const [hasDocument, setHasDocument] = useState<boolean | undefined>();
  const [lowStock, setLowStock] = useState<boolean | undefined>();
  const [isInShop, setIsInShop] = useState<boolean | undefined>();

  const { dateFrom, dateTo } = useMemo(() => {
    if (datePreset === 'custom') {
      return { dateFrom: customFrom, dateTo: customTo };
    }
    const resolved = getDateRangeFromPreset(datePreset);
    return { dateFrom: resolved.startAt, dateTo: resolved.endAt };
  }, [datePreset, customFrom, customTo]);

  const baseRequest = useMemo(
    () => ({
      name: name || undefined,
      reference: reference || undefined,
      partNumber: partNumber || undefined,
      nominalCode: nominalCode || undefined,
      supplierInvoiceNumber: supplierInvoiceNumber || undefined,
      categoryIds,
      supplierIds,
      dateFrom,
      dateTo,
      dateField,
      hasDocument,
      lowStock,
      isInShop,
    }),
    [name, reference, partNumber, nominalCode, supplierInvoiceNumber, categoryIds, supplierIds, dateFrom, dateTo, dateField, hasDocument, lowStock, isInShop]
  );

  useEffect(() => {
    inventoryStore.getAll({ ...baseRequest, maxResultCount, skipCount });
  }, [baseRequest, maxResultCount, skipCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (pagination: any) => {
    inventoryStore.handlePageChange(pagination);
  };

  const resetPage = () => {
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleNameSearch = useMemo(() => debounce((text: string) => {
    setName(text);
    resetPage();
  }, 500), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReferenceSearch = useMemo(() => debounce((text: string) => {
    setReference(text);
    resetPage();
  }, 500), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePartNumberSearch = useMemo(() => debounce((text: string) => {
    setPartNumber(text);
    resetPage();
  }, 500), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNominalCodeSearch = useMemo(() => debounce((text: string) => {
    setNominalCode(text);
    resetPage();
  }, 500), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvoiceNumberSearch = useMemo(() => debounce((text: string) => {
    setSupplierInvoiceNumber(text);
    resetPage();
  }, 500), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryFilter = useCallback((values: string[] | undefined) => {
    setCategoryIds(values && values.length ? values : undefined);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierFilter = useCallback((values: string[] | undefined) => {
    setSupplierIds(values && values.length ? values : undefined);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDatePresetChange = useCallback((preset: string | undefined) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomDateRange = useCallback((from: string | undefined, to: string | undefined) => {
    setCustomFrom(from);
    setCustomTo(to);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateFieldChange = useCallback((field: InventoryDateField) => {
    setDateField(field);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHasDocumentFilter = useCallback((value: boolean | undefined) => {
    setHasDocument(value);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLowStockFilter = useCallback((value: boolean | undefined) => {
    setLowStock(value);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIsInShopFilter = useCallback((value: boolean | undefined) => {
    setIsInShop(value);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    inventoryStore.getAll({ ...baseRequest, maxResultCount, skipCount });
  }, [baseRequest, maxResultCount, skipCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllForExport = useCallback(async () => {
    const result = await InventoryService.getAll({
      ...baseRequest,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [baseRequest, totalCount]);

  return {
    loading,
    dataSource: Array.from(inventories.values()),
    totalCount,
    currentPage,
    maxResultCount,
    dateField,
    datePreset,
    customFrom,
    customTo,
    categoryIds,
    supplierIds,
    handlePageChange,
    handleNameSearch,
    handleReferenceSearch,
    handlePartNumberSearch,
    handleNominalCodeSearch,
    handleInvoiceNumberSearch,
    handleCategoryFilter,
    handleSupplierFilter,
    handleDatePresetChange,
    handleCustomDateRange,
    handleDateFieldChange,
    handleHasDocumentFilter,
    handleLowStockFilter,
    handleIsInShopFilter,
    refresh,
    fetchAllForExport,
  };
};
